import { useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { InputText } from '@/components/commons/input-text'
import { SearchLgIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Skeleton } from '@/components/ui/skeleton'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { NeighborhoodDetail } from './neighborhood-detail'

interface NeighborhoodSearchProps {
	onNeighborhoodClick: (neighborhoodId: string) => void
	isLoading?: boolean
}

export const NeighborhoodSearch = ({
	onNeighborhoodClick,
	isLoading = false
}: NeighborhoodSearchProps) => {
	const { t } = useTranslation()
	const { neighborhoods, subgeos } = useSubgeoStore()
	const [search, setSearch] = useState('')
	const [inputValue, setInputValue] = useState('')
	const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<
		string | null
	>(null)

	const onSearch = debounce((value: string) => {
		setSearch(value)
	}, 350)

	const neighborhoodsWithSubgeo = useMemo(() => {
		return neighborhoods.map((neighborhood) => {
			const parentSubgeo = subgeos.find((s) =>
				s.neighborhoodIds.includes(neighborhood.acp_id)
			)
			return {
				id: neighborhood.acp_id,
				name: neighborhood.place_name,
				subgeoName: parentSubgeo?.name || null
			}
		})
	}, [neighborhoods, subgeos])

	const filteredNeighborhoods = useMemo(() => {
		if (!search) {
			return neighborhoodsWithSubgeo
		}
		const searchLower = search.toLowerCase()
		return neighborhoodsWithSubgeo.filter((n) =>
			n.name.toLowerCase().includes(searchLower)
		)
	}, [neighborhoodsWithSubgeo, search])

	const selectedNeighborhood = useMemo(() => {
		if (!selectedNeighborhoodId) {
			return null
		}
		return neighborhoodsWithSubgeo.find(
			(n) => n.id === selectedNeighborhoodId
		)
	}, [selectedNeighborhoodId, neighborhoodsWithSubgeo])

	const currentSubgeo = useMemo(() => {
		if (!selectedNeighborhood) {
			return null
		}
		return subgeos.find((s) =>
			s.neighborhoodIds.includes(selectedNeighborhood.id)
		)
	}, [selectedNeighborhood, subgeos])

	const handleNeighborhoodClick = (id: string) => {
		setSelectedNeighborhoodId(id)
		onNeighborhoodClick(id)
	}

	const handleBack = () => {
		setSelectedNeighborhoodId(null)
		setSearch('')
		setInputValue('')
	}

	if (selectedNeighborhood) {
		return (
			<NeighborhoodDetail
				currentSubgeo={currentSubgeo ?? null}
				neighborhoodId={selectedNeighborhood.id}
				neighborhoodName={selectedNeighborhood.name}
				onBack={handleBack}
				subgeos={subgeos}
			/>
		)
	}

	return (
		<div className='flex min-h-0 w-full flex-1 flex-col gap-2'>
			<div className='flex w-full flex-col items-start gap-2 px-2.5 pt-2.5'>
				<h6 className='font-medium text-neutral-500 text-sm'>
					{isLoading ? (
						<Skeleton height={20} width={150} />
					) : (
						t('subgeo:neighborhoods_count', {
							count: neighborhoods.length
						})
					)}
				</h6>
				<InputText
					disabled={isLoading}
					inputSize='sm'
					onChange={(e) => {
						setInputValue(e.target.value)
						onSearch(e.target.value)
					}}
					placeholder={t('subgeo:search_neighborhoods')}
					type='text'
					typeIcon={<SearchLgIcon size={16} />}
					value={inputValue}
				/>
				{!isLoading && filteredNeighborhoods.length === 0 && (
					<InputHelpText
						helpText={t('subgeo:no_neighborhoods_found')}
					/>
				)}
			</div>

			<div className='min-h-0 w-full flex-1 overflow-hidden px-2.5 pb-2.5'>
				{isLoading ? (
					<div className='flex w-full flex-col gap-1.5'>
						{Array.from(
							{ length: 8 },
							(_, i) => `skeleton-${i}`
						).map((key) => (
							<div
								className='flex w-full items-center gap-2'
								key={key}
							>
								<Skeleton height={48} width='100%' />
							</div>
						))}
					</div>
				) : (
					<Virtuoso
						className='[&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:hover:bg-neutral-400 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
						data={filteredNeighborhoods}
						itemContent={(_, item) => (
							<button
								className='flex w-full cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent p-1.5 text-left transition-colors hover:bg-neutral-50'
								key={item.id}
								onClick={() => handleNeighborhoodClick(item.id)}
								type='button'
							>
								<div className='flex w-full flex-col justify-between font-medium text-neutral-900 text-xs'>
									<span className='line-clamp-2 max-w-[260px] overflow-hidden text-ellipsis whitespace-break-spaces'>
										{item.name}
									</span>
									<div className='font-normal text-[11px] text-neutral-500'>
										{item.subgeoName ||
											t('subgeo:unassigned')}
									</div>
								</div>
							</button>
						)}
						overscan={100}
						totalCount={filteredNeighborhoods.length}
					/>
				)}
			</div>
		</div>
	)
}
